import { useState, useRef, useEffect, useMemo } from "react";
import { autofillOffProps } from "../../../shared/autofillOffProps";

/**
 * Drawer içinde kalan, tema ile uyumlu arama-yapılabilir seçim.
 * Native <datalist> yerine kullanılır (taşma / tarayıcı autofill sorunları olmaz).
 */
export default function FilterCombobox({
    value,
    onChange,
    options,
    placeholder,
    id,
    className = "",
    /** Filtre çekmecesinde kayıtlı adres/telefon önerilerini kesmek için readOnly kilit + sıfırlama */
    autofillGate = false,
    /** Drawer açıldığında (false→true) düzenleme kilidi yenilenir */
    filterDrawerOpen = false,
}) {
    const [open, setOpen] = useState(false);
    const [editable, setEditable] = useState(!autofillGate);
    const rootRef = useRef(null);
    const prevDrawerRef = useRef(filterDrawerOpen);

    useEffect(() => {
        if (!autofillGate) return;
        const wasOpen = prevDrawerRef.current;
        prevDrawerRef.current = filterDrawerOpen;
        if (filterDrawerOpen && !wasOpen) setEditable(false);
    }, [filterDrawerOpen, autofillGate]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const q = (value || "").trim().toLowerCase();

    const filtered = useMemo(() => {
        if (!q) return options.slice(0, 80);
        return options
            .filter(
                (o) =>
                    o.label.toLowerCase().includes(q) ||
                    String(o.value).toLowerCase().includes(q)
            )
            .slice(0, 80);
    }, [options, q]);

    const pick = (v) => {
        onChange(v);
        setOpen(false);
    };

    return (
        <div ref={rootRef} className={`filter-combobox ${className}`}>
            <input
                id={id}
                type="search"
                className="filter-input filter-combobox-input"
                value={value || ""}
                readOnly={autofillGate ? !editable : false}
                onMouseDown={() => autofillGate && setEditable(true)}
                onChange={(e) => {
                    onChange(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => {
                    if (autofillGate) setEditable(true);
                    setOpen(true);
                }}
                placeholder={placeholder}
                name={autofillGate ? (id ? `ftx-cmb-${String(id).replace(/[^a-z0-9-]/gi, "")}` : "ftx-cmb") : undefined}
                {...autofillOffProps}
            />
            {open && (
                <ul className="filter-combobox-list" role="listbox">
                    {filtered.length === 0 ? (
                        <li className="filter-combobox-item filter-combobox-empty" role="presentation">
                            Sonuç yok
                        </li>
                    ) : (
                        filtered.map((o) => (
                            <li
                                key={`${o.value}-${o.label}`}
                                role="option"
                                className={`filter-combobox-item${value === o.value ? " is-active" : ""}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => pick(o.value)}
                            >
                                <span className="filter-combobox-item-label">{o.label}</span>
                                {o.value !== o.label && (
                                    <span className="filter-combobox-item-meta">{o.value}</span>
                                )}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}
